import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type {
  CompanySearchFilters,
  IdealCustomerProfile,
  LeadSource,
} from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { getCompanyTechnologies } from "@/api/companies";
import { EMPLOYEE_RANGES } from "@/components/people/people-constants";
import {
  COMPANY_FILTER_SECTIONS,
  defaultCompanyOpenMap,
  type CompanyFilterSectionId,
} from "./company-filter-config";
import { RailIconButton } from "@/components/ui/rail-icon-button";
import {
  WORKSPACE_SOURCE_OPTIONS,
  dateInputToCreatedAfter,
  dateInputToCreatedBefore,
  isoToDateInput,
} from "@/lib/workspace-source";

export type CompanyFilterState = {
  personaIcpId?: string;
  qOrganizationName: string;
  organizationDomains: string[];
  organizationIds: string[];
  organizationLocations: string[];
  organizationNotLocations: string[];
  organizationNumEmployeesRanges: string[];
  revenueMin?: number;
  revenueMax?: number;
  qOrganizationKeywordTags: string[];
  organizationIndustryTagIds: string[];
  currentlyUsingAnyOfTechnologyUids: string[];
  latestFundingAmountMin?: number;
  latestFundingAmountMax?: number;
  totalFundingMin?: number;
  totalFundingMax?: number;
  latestFundingDateMin: string;
  latestFundingDateMax: string;
  qOrganizationJobTitles: string[];
  organizationJobLocations: string[];
  organizationNumJobsMin?: number;
  organizationNumJobsMax?: number;
  organizationJobPostedAtMin: string;
  organizationJobPostedAtMax: string;
  /** Workspace Accounts only */
  sources: LeadSource[];
  createdAfter: string;
  createdBefore: string;
};

export const EMPTY_COMPANY_FILTERS: CompanyFilterState = {
  personaIcpId: undefined,
  qOrganizationName: "",
  organizationDomains: [],
  organizationIds: [],
  organizationLocations: [],
  organizationNotLocations: [],
  organizationNumEmployeesRanges: [],
  revenueMin: undefined,
  revenueMax: undefined,
  qOrganizationKeywordTags: [],
  organizationIndustryTagIds: [],
  currentlyUsingAnyOfTechnologyUids: [],
  latestFundingAmountMin: undefined,
  latestFundingAmountMax: undefined,
  totalFundingMin: undefined,
  totalFundingMax: undefined,
  latestFundingDateMin: "",
  latestFundingDateMax: "",
  qOrganizationJobTitles: [],
  organizationJobLocations: [],
  organizationNumJobsMin: undefined,
  organizationNumJobsMax: undefined,
  organizationJobPostedAtMin: "",
  organizationJobPostedAtMax: "",
  sources: [],
  createdAfter: "",
  createdBefore: "",
};

export function filtersToApi(
  f: CompanyFilterState,
  page: number,
  perPage: number,
): CompanySearchFilters {
  return {
    qOrganizationName: f.qOrganizationName.trim() || undefined,
    organizationDomains: f.organizationDomains,
    organizationIds: f.organizationIds,
    organizationLocations: f.organizationLocations,
    organizationNotLocations: f.organizationNotLocations,
    organizationNumEmployeesRanges: f.organizationNumEmployeesRanges,
    revenueMin: f.revenueMin,
    revenueMax: f.revenueMax,
    qOrganizationKeywordTags: f.qOrganizationKeywordTags,
    organizationIndustryTagIds: f.organizationIndustryTagIds,
    currentlyUsingAnyOfTechnologyUids: f.currentlyUsingAnyOfTechnologyUids,
    latestFundingAmountMin: f.latestFundingAmountMin,
    latestFundingAmountMax: f.latestFundingAmountMax,
    totalFundingMin: f.totalFundingMin,
    totalFundingMax: f.totalFundingMax,
    latestFundingDateMin: f.latestFundingDateMin || undefined,
    latestFundingDateMax: f.latestFundingDateMax || undefined,
    qOrganizationJobTitles: f.qOrganizationJobTitles,
    organizationJobLocations: f.organizationJobLocations,
    organizationNumJobsMin: f.organizationNumJobsMin,
    organizationNumJobsMax: f.organizationNumJobsMax,
    organizationJobPostedAtMin: f.organizationJobPostedAtMin || undefined,
    organizationJobPostedAtMax: f.organizationJobPostedAtMax || undefined,
    personaIcpId: f.personaIcpId,
    sources: f.sources.length ? f.sources : undefined,
    createdAfter: dateInputToCreatedAfter(f.createdAfter),
    createdBefore: dateInputToCreatedBefore(f.createdBefore),
    page,
    perPage,
  };
}

export function apiFiltersToState(
  f: CompanySearchFilters,
): CompanyFilterState {
  return {
    ...EMPTY_COMPANY_FILTERS,
    personaIcpId: f.personaIcpId,
    qOrganizationName: f.qOrganizationName ?? "",
    organizationDomains: f.organizationDomains ?? [],
    organizationIds: f.organizationIds ?? [],
    organizationLocations: f.organizationLocations ?? [],
    organizationNotLocations: f.organizationNotLocations ?? [],
    organizationNumEmployeesRanges: f.organizationNumEmployeesRanges ?? [],
    revenueMin: f.revenueMin,
    revenueMax: f.revenueMax,
    qOrganizationKeywordTags: f.qOrganizationKeywordTags ?? [],
    organizationIndustryTagIds: f.organizationIndustryTagIds ?? [],
    currentlyUsingAnyOfTechnologyUids:
      f.currentlyUsingAnyOfTechnologyUids ?? [],
    latestFundingAmountMin: f.latestFundingAmountMin,
    latestFundingAmountMax: f.latestFundingAmountMax,
    totalFundingMin: f.totalFundingMin,
    totalFundingMax: f.totalFundingMax,
    latestFundingDateMin: f.latestFundingDateMin ?? "",
    latestFundingDateMax: f.latestFundingDateMax ?? "",
    qOrganizationJobTitles: f.qOrganizationJobTitles ?? [],
    organizationJobLocations: f.organizationJobLocations ?? [],
    organizationNumJobsMin: f.organizationNumJobsMin,
    organizationNumJobsMax: f.organizationNumJobsMax,
    organizationJobPostedAtMin: f.organizationJobPostedAtMin ?? "",
    organizationJobPostedAtMax: f.organizationJobPostedAtMax ?? "",
    sources: f.sources ?? [],
    createdAfter: isoToDateInput(f.createdAfter),
    createdBefore: isoToDateInput(f.createdBefore),
  };
}

export function seedCompanyFiltersFromIcp(
  icp: IdealCustomerProfile,
  prev: CompanyFilterState = EMPTY_COMPANY_FILTERS,
): CompanyFilterState {
  return {
    ...prev,
    personaIcpId: icp.id,
    organizationLocations: icp.geographies?.length
      ? [...icp.geographies]
      : prev.organizationLocations,
    qOrganizationKeywordTags: [
      ...(icp.industries ?? []),
      ...(icp.icpKeywords ?? []),
    ],
  };
}

function TagInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
  }
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap gap-1 rounded-md bg-surface-hover p-1.5">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="rounded bg-surface px-1.5 py-0.5 text-[10px] hover:bg-card"
          >
            {v} ×
          </button>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={placeholder}
          className="min-w-[6rem] flex-1 bg-transparent px-1 py-0.5 text-xs outline-none"
        />
      </div>
    </label>
  );
}

function ChipMulti({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ id: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() =>
                onChange(
                  active
                    ? selected.filter((x) => x !== opt.id)
                    : [...selected, opt.id],
                )
              }
              className={
                active
                  ? "rounded bg-brand px-1.5 py-0.5 text-[10px] font-medium text-brand-foreground"
                  : "rounded bg-surface-hover px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FilterGroup({
  title,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-xs font-semibold text-foreground hover:bg-surface-hover"
      >
        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{title}</span>
        <ChevronDown
          className={cn(
            "size-3.5 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <div className="space-y-3.5 px-4 pb-4">{children}</div>}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
    </label>
  );
}

function NumField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value?: number;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : Number(raw));
        }}
        placeholder={placeholder}
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
    </label>
  );
}

function TechTypeahead({
  label,
  values,
  onChange,
  technologies,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  technologies: string[];
}) {
  const [q, setQ] = useState("");
  const suggestions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return technologies
      .filter((t) => t.toLowerCase().includes(needle) && !values.includes(t))
      .slice(0, 8);
  }, [q, technologies, values]);

  return (
    <div className="space-y-1">
      <TagInput
        label={label}
        values={values}
        onChange={onChange}
        placeholder="Type tech uid…"
      />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search technologies…"
        className="w-full rounded-md bg-surface-hover px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-border"
      />
      {suggestions.length > 0 && (
        <ul className="max-h-28 overflow-y-auto rounded-md bg-popover text-xs shadow-lg ring-1 ring-black/5 dark:ring-white/10">
          {suggestions.map((t) => (
            <li key={t}>
              <button
                type="button"
                className="w-full px-2 py-1 text-left hover:bg-surface-hover"
                onClick={() => {
                  onChange([...values, t]);
                  setQ("");
                }}
              >
                {t}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CompanyFilters({
  value,
  onChange,
  onSearch,
  onClear,
  searching,
  collapsed,
  onToggleCollapsed,
  icps = [],
  activeIcpId,
  embedded = false,
  workspaceScope = false,
}: {
  value: CompanyFilterState;
  onChange: (next: CompanyFilterState) => void;
  onSearch: () => void;
  /** When set, Clear filters resets and re-runs search (preferred over onChange alone). */
  onClear?: () => void;
  searching?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  icps?: IdealCustomerProfile[];
  activeIcpId?: string;
  /** Render without sticky sidebar chrome (for mobile sheets). */
  embedded?: boolean;
  /** Show Accounts-only filters (source, created date). */
  workspaceScope?: boolean;
}) {
  const [openMap, setOpenMap] = useState(defaultCompanyOpenMap);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data: techData } = useQuery({
    queryKey: ["company-technologies"],
    queryFn: getCompanyTechnologies,
    staleTime: 300_000,
    retry: false,
    enabled: showAdvanced || openMap.technologies,
  });
  const technologies = techData?.technologies ?? [];

  const visibleSections = COMPANY_FILTER_SECTIONS.filter(
    (s) =>
      (showAdvanced || !s.advanced) &&
      (!s.workspaceOnly || workspaceScope),
  );

  function setOpen(id: CompanyFilterSectionId, open: boolean) {
    setOpenMap((m) => ({ ...m, [id]: open }));
  }

  function expandAll() {
    setShowAdvanced(true);
    setOpenMap(
      Object.fromEntries(
        COMPANY_FILTER_SECTIONS.map((s) => [s.id, true]),
      ) as Record<CompanyFilterSectionId, boolean>,
    );
  }

  function collapseAll() {
    setOpenMap(
      Object.fromEntries(
        COMPANY_FILTER_SECTIONS.map((s) => [s.id, false]),
      ) as Record<CompanyFilterSectionId, boolean>,
    );
  }

  if (collapsed && !embedded) {
    const collapsedSections = COMPANY_FILTER_SECTIONS.filter(
      (s) => !s.advanced && (!s.workspaceOnly || workspaceScope),
    );
    return (
      <aside className="sticky top-0 flex h-full w-12 shrink-0 flex-col items-center gap-0.5 overflow-y-auto rounded-xl border border-border bg-card py-3">
        <RailIconButton label="Show filters" onClick={onToggleCollapsed} className="mb-1">
          <PanelLeftOpen className="size-4" />
        </RailIconButton>
        <div className="mb-1 w-6 border-t border-border" />
        {collapsedSections.map((section) => {
          const Icon = section.icon;
          return (
            <RailIconButton
              key={section.id}
              label={section.label}
              onClick={() => {
                setOpen(section.id, true);
                onToggleCollapsed?.();
              }}
            >
              <Icon className="size-4" />
            </RailIconButton>
          );
        })}
      </aside>
    );
  }

  function renderSection(id: CompanyFilterSectionId) {
    switch (id) {
      case "personas":
        return (
          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Apply an ICP to seed locations and industry / keyword tags.
            </p>
            {icps.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No ICPs yet. Create one under Settings → Personas.
              </p>
            ) : (
              <ul className="space-y-1">
                {icps.map((icp) => {
                  const active = value.personaIcpId === icp.id;
                  const isDefault = icp.id === activeIcpId;
                  return (
                    <li key={icp.id}>
                      <button
                        type="button"
                        onClick={() =>
                          onChange(seedCompanyFiltersFromIcp(icp, value))
                        }
                        className={cn(
                          "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs",
                          active
                            ? "bg-brand/10 text-foreground ring-1 ring-brand/40"
                            : "hover:bg-surface-hover",
                        )}
                      >
                        <span className="truncate font-medium">{icp.name}</span>
                        {isDefault && (
                          <span className="text-[10px] text-muted-foreground">
                            Active
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      case "nameKeywords":
        return (
          <>
            <TextField
              label="Company name"
              value={value.qOrganizationName}
              onChange={(qOrganizationName) =>
                onChange({ ...value, qOrganizationName })
              }
              placeholder="Acme"
            />
            <TagInput
              label="Keyword tags"
              values={value.qOrganizationKeywordTags}
              onChange={(qOrganizationKeywordTags) =>
                onChange({ ...value, qOrganizationKeywordTags })
              }
              placeholder="saas, fintech…"
            />
          </>
        );
      case "location":
        return (
          <TagInput
            label="HQ locations"
            values={value.organizationLocations}
            onChange={(organizationLocations) =>
              onChange({ ...value, organizationLocations })
            }
            placeholder="United States, California…"
          />
        );
      case "employees":
        return (
          <ChipMulti
            label="# Employees"
            options={EMPLOYEE_RANGES}
            selected={value.organizationNumEmployeesRanges}
            onChange={(organizationNumEmployeesRanges) =>
              onChange({ ...value, organizationNumEmployeesRanges })
            }
          />
        );
      case "industry":
        return (
          <TagInput
            label="Industry tag IDs"
            values={value.organizationIndustryTagIds}
            onChange={(organizationIndustryTagIds) =>
              onChange({ ...value, organizationIndustryTagIds })
            }
            placeholder="Apollo industry tag ids"
          />
        );
      case "source":
        return (
          <>
            <ChipMulti
              label="Source"
              options={WORKSPACE_SOURCE_OPTIONS}
              selected={value.sources}
              onChange={(sources) =>
                onChange({ ...value, sources: sources as LeadSource[] })
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="Created from"
                type="date"
                value={value.createdAfter}
                onChange={(createdAfter) =>
                  onChange({ ...value, createdAfter })
                }
              />
              <TextField
                label="Created to"
                type="date"
                value={value.createdBefore}
                onChange={(createdBefore) =>
                  onChange({ ...value, createdBefore })
                }
              />
            </div>
          </>
        );
      case "excludeLocations":
        return (
          <TagInput
            label="Exclude HQ locations"
            values={value.organizationNotLocations}
            onChange={(organizationNotLocations) =>
              onChange({ ...value, organizationNotLocations })
            }
            placeholder="ireland, minnesota…"
          />
        );
      case "domains":
        return (
          <>
            <TagInput
              label="Domains"
              values={value.organizationDomains}
              onChange={(organizationDomains) =>
                onChange({ ...value, organizationDomains })
              }
              placeholder="acme.com"
            />
            <TagInput
              label="Organization IDs"
              values={value.organizationIds}
              onChange={(organizationIds) =>
                onChange({ ...value, organizationIds })
              }
              placeholder="Apollo org id"
            />
          </>
        );
      case "revenue":
        return (
          <div className="grid grid-cols-2 gap-2">
            <NumField
              label="Min"
              value={value.revenueMin}
              onChange={(revenueMin) => onChange({ ...value, revenueMin })}
            />
            <NumField
              label="Max"
              value={value.revenueMax}
              onChange={(revenueMax) => onChange({ ...value, revenueMax })}
            />
          </div>
        );
      case "technologies":
        return (
          <TechTypeahead
            label="Using any of"
            values={value.currentlyUsingAnyOfTechnologyUids}
            onChange={(currentlyUsingAnyOfTechnologyUids) =>
              onChange({ ...value, currentlyUsingAnyOfTechnologyUids })
            }
            technologies={technologies}
          />
        );
      case "funding":
        return (
          <>
            <div className="grid grid-cols-2 gap-2">
              <NumField
                label="Latest funding min"
                value={value.latestFundingAmountMin}
                onChange={(latestFundingAmountMin) =>
                  onChange({ ...value, latestFundingAmountMin })
                }
              />
              <NumField
                label="Latest funding max"
                value={value.latestFundingAmountMax}
                onChange={(latestFundingAmountMax) =>
                  onChange({ ...value, latestFundingAmountMax })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NumField
                label="Total funding min"
                value={value.totalFundingMin}
                onChange={(totalFundingMin) =>
                  onChange({ ...value, totalFundingMin })
                }
              />
              <NumField
                label="Total funding max"
                value={value.totalFundingMax}
                onChange={(totalFundingMax) =>
                  onChange({ ...value, totalFundingMax })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="Funded from"
                type="date"
                value={value.latestFundingDateMin}
                onChange={(latestFundingDateMin) =>
                  onChange({ ...value, latestFundingDateMin })
                }
              />
              <TextField
                label="Funded to"
                type="date"
                value={value.latestFundingDateMax}
                onChange={(latestFundingDateMax) =>
                  onChange({ ...value, latestFundingDateMax })
                }
              />
            </div>
          </>
        );
      case "hiring":
        return (
          <>
            <TagInput
              label="Job titles"
              values={value.qOrganizationJobTitles}
              onChange={(qOrganizationJobTitles) =>
                onChange({ ...value, qOrganizationJobTitles })
              }
              placeholder="sales manager…"
            />
            <TagInput
              label="Job locations"
              values={value.organizationJobLocations}
              onChange={(organizationJobLocations) =>
                onChange({ ...value, organizationJobLocations })
              }
              placeholder="Remote, NYC"
            />
            <div className="grid grid-cols-2 gap-2">
              <NumField
                label="Jobs min"
                value={value.organizationNumJobsMin}
                onChange={(organizationNumJobsMin) =>
                  onChange({ ...value, organizationNumJobsMin })
                }
              />
              <NumField
                label="Jobs max"
                value={value.organizationNumJobsMax}
                onChange={(organizationNumJobsMax) =>
                  onChange({ ...value, organizationNumJobsMax })
                }
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="Posted from"
                type="date"
                value={value.organizationJobPostedAtMin}
                onChange={(organizationJobPostedAtMin) =>
                  onChange({ ...value, organizationJobPostedAtMin })
                }
              />
              <TextField
                label="Posted to"
                type="date"
                value={value.organizationJobPostedAtMax}
                onChange={(organizationJobPostedAtMax) =>
                  onChange({ ...value, organizationJobPostedAtMax })
                }
              />
            </div>
          </>
        );
      default:
        return null;
    }
  }

  return (
    <aside
      className={cn(
        "flex flex-col overflow-hidden bg-card",
        embedded
          ? "h-full w-full border-0"
          : "sticky top-0 h-full w-72 shrink-0 rounded-xl border border-border",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border px-4 py-3.5">
        {!embedded && (
          <h2 className="mr-auto text-sm font-semibold text-foreground">
            Filters
          </h2>
        )}
        {embedded && <div className="mr-auto" />}
        <button
          type="button"
          onClick={expandAll}
          className="text-[11px] text-brand hover:underline"
        >
          Expand
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          Collapse
        </button>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-[11px] text-muted-foreground hover:text-foreground"
        >
          {showAdvanced ? "Fewer" : "More"}
        </button>
        {onToggleCollapsed && !embedded && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            title="Hide filters"
            className="rounded-md p-1 text-muted-foreground hover:bg-surface-hover hover:text-foreground"
          >
            <PanelLeftClose className="size-3.5" />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {visibleSections.map((section) => (
          <FilterGroup
            key={section.id}
            title={section.label}
            icon={section.icon}
            open={!!openMap[section.id]}
            onToggle={() => setOpen(section.id, !openMap[section.id])}
          >
            {renderSection(section.id)}
          </FilterGroup>
        ))}
      </div>

      <div className="space-y-2.5 border-t border-border p-4">
        <button
          type="button"
          onClick={onSearch}
          disabled={searching}
          className="w-full rounded-md bg-brand px-3 py-2.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
        >
          {searching ? "Searching…" : "Apply filters"}
        </button>
        <button
          type="button"
          onClick={() =>
            onClear ? onClear() : onChange(EMPTY_COMPANY_FILTERS)
          }
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
        >
          Clear filters
        </button>
      </div>
    </aside>
  );
}
